import React from "react";

import {
  CalendarIcon as Calendar,
  CancelIcon as Cancel,
  LoadingIcon,
  PendingIcon,
  StagedIcon,
  SuccessIcon,
  WarningIcon,
  MinusIcon,
  StagedFailedIcon
} from "@packages/ds";
import { useIntl } from "react-intl";

import { Tooltip } from "ds/Tooltip";

import * as S from "./StatusIcon.styles";

import type { Activity as TActivity } from "@packages/client";

type StatusIconProps = {
  state: string;
  result: string;
  viewLogs?: () => void;
  type?: string;
  activity?: TActivity;
};

const StatusIcon = ({
  activity,
  state,
  result,
  viewLogs,
  type
}: StatusIconProps) => {
  const intl = useIntl();

  let icon: React.ReactNode = null;
  let tooltipText: string | undefined = undefined;
  let align: "center" | "end" | "start" | undefined = undefined;
  let alignOffset: number | undefined = undefined;
  let pointerPosition:
    | `fromLeft=${string}`
    | `fromRight=${string}`
    | undefined = undefined;

  if (state === "in_progress") {
    icon = <LoadingIcon data-testid="loading_activity" />;
  }

  if (state === "pending") {
    icon = (
      <PendingIcon
        color="var(--theme-warning-600)"
        data-testid="pending_activity"
      />
    );
  }
  if (state === "staged" && result === "failure") {
    align = "end";
    pointerPosition = "fromRight=0px";
    tooltipText = "activities.staged.failed.tooltip";
    alignOffset = -5;
    icon = (
      <StagedFailedIcon
        data-testid="staged_activity_failure"
        stroke="var(--theme-neutral-400)"
      />
    );
  }
  if (state === "staged" && result !== "failure") {
    align = "end";
    pointerPosition = "fromRight=0px";
    tooltipText = "activities.staged.tooltip";
    alignOffset = -5;
    icon = (
      <StagedIcon
        data-testid="staged_activity"
        stroke="var(--theme-neutral-400)"
      />
    );
  }
  if (state === "scheduled") {
    icon = <Calendar color="var(--icon-granite-fill,var(--granite))" />;
  }

  if (state === "complete" && result === "success") {
    icon = <SuccessIcon color="var(--theme-success-600)" />;
  }
  if (
    state === "complete" &&
    result === "success" &&
    type === "environment.alert"
  ) {
    const envResourceState =
      activity?.payload.environment_resources_update_activity.state;
    tooltipText =
      envResourceState === "skipped" ? "no.scaling.tooltip" : "scaling.tooltip";
    align = "end";

    icon =
      envResourceState === "skipped" ? (
        <MinusIcon color="var(--mode-vector-neutral-default)" />
      ) : (
        <WarningIcon color="var(--theme-warning-600)" />
      );
  }

  if (state === "complete" && result === "cancelled") {
    icon = <Cancel color="var(--mode-vector-neutral-default)" />;
  }

  if (state === "complete" && result === "failure") {
    icon = (
      <WarningIcon
        color="var(--theme-failure-600)"
        data-testid="warning_activity"
      />
    );
  }

  if (state === "cancelled" && result === "failure") {
    icon = <Cancel color="var(--mode-vector-critical-default)" />;
  }

  return (
    <Tooltip
      tooltip={
        tooltipText
          ? intl.formatMessage({
              id: tooltipText
            })
          : state === "complete"
            ? result
            : state.replace("_", " ")
      }
      analyticId="status_icon"
      align={align}
      pointerPosition={pointerPosition}
      alignOffset={alignOffset}
    >
      <S.Button variant="link" onClick={viewLogs} aria-label="view-logs">
        {icon}
      </S.Button>
    </Tooltip>
  );
};

export default StatusIcon;
